import {
  toCompanySectionsState,
  type CompanySectionsState,
} from "@/contexts/company-sections-context";
import type { SSEError } from "@/hooks/useApi";
import {
  companyPageProgress,
  companyPageStore,
  type CompanyPageGet,
  type CompanyPageState,
} from "@/stores/company-page-store";
import type { CompanyDTO } from "@shaarp/common";

/** What a side panel, a mobile body or a table row reads of a company's run. */
export type CompanyEnrichmentView = {
  latestData: CompanyDTO | null;
  progress: number;
  isActive: boolean;
  /** The run went to its end and enriched: no refusal, no failure, budget left. */
  hasCompleted: boolean;
  /** The run ended on the team's spent enrichment budget: only the stored row. */
  budgetSpent: boolean;
  /** A refusal (403, 429) or a failed stream, with the API's `status` and `code`. */
  error: SSEError | null;
  /** Each section's status, for the cards gated on them. */
  sections: CompanySectionsState;
  /**
   * An enrichment asked from the side panel (`start`) is running. The table
   * rows spin for that one only — not for the run every opening of the page
   * starts on its own.
   */
  isEnriching: boolean;
};

/**
 * The view of `state`, or of no run at all — which an entry its page left
 * mid-run reads as. Its company is the latest data only while no early end
 * left it behind the row the server went on to write (`isCompanyBehind`): a
 * stream cut, failed or refused, or a run that carries such a company until
 * its snapshot.
 */
export function toCompanyEnrichmentView(
  state: CompanyPageState | undefined,
  isManualRun = false,
): CompanyEnrichmentView {
  if (!state || state.isAbandoned) {
    return {
      latestData: null,
      progress: 0,
      isActive: false,
      hasCompleted: false,
      budgetSpent: false,
      error: null,
      sections: toCompanySectionsState(undefined),
      isEnriching: false,
    };
  }
  return {
    latestData: state.isCompanyBehind ? null : state.company,
    progress: companyPageProgress(state),
    isActive: !state.done,
    hasCompleted: state.done && state.error === null && !state.budgetSpent,
    budgetSpent: state.budgetSpent,
    error: state.error,
    sections: toCompanySectionsState(state),
    isEnriching: isManualRun && !state.done,
  };
}

type ManualRun = { teamId: string; companyId: string; release: () => void };

function manualRunKey(teamId: string, companyId: string): string {
  return `${teamId}|${companyId}`;
}

/** The enrichments asked from the side panel that have not ended yet. */
const manualRuns = new Map<string, ManualRun>();
let stopWatchingRuns: (() => void) | null = null;

/**
 * Lets go of each manual run once its entry ended — or was evicted — and
 * stops watching the store when none is left.
 */
function settleManualRuns(): void {
  for (const [key, run] of manualRuns) {
    const state = companyPageStore.getState(run.teamId, run.companyId);
    if (state && !state.done) continue;
    manualRuns.delete(key);
    run.release();
  }
  if (manualRuns.size === 0) {
    stopWatchingRuns?.();
    stopWatchingRuns = null;
  }
}

/**
 * The manual enrichment of the side panel, over the company page's own run
 * (`companyPageStore`): one stream per company, whichever view asked first,
 * through `useApiStream` like every call (`docs/decisions/0003`), keyed by the
 * team it is sent for like the page. `start` joins the run in progress or runs
 * the company again once it finished, and holds a view of it until it ends:
 * closing the panel, or leaving the page it joined, does not cut it.
 */
export const companyEnrichmentStore = {
  subscribe: companyPageStore.subscribe,
  getState(teamId: string, companyId: string): CompanyPageState | undefined {
    return companyPageStore.getState(teamId, companyId);
  },
  /** Whether the run of the company is one the side panel asked for. */
  isManualRun(teamId: string, companyId: string): boolean {
    return manualRuns.has(manualRunKey(teamId, companyId));
  },
  start(teamId: string, companyId: string, get: CompanyPageGet): Promise<void> {
    // Started first: the run's entry is live by the time it is watched, so a
    // run started on a finished entry is not let go of on the spot.
    const started = companyPageStore.start(teamId, companyId, get);
    const key = manualRunKey(teamId, companyId);
    if (!manualRuns.has(key)) {
      manualRuns.set(key, {
        teamId,
        companyId,
        release: companyPageStore.retain(teamId, companyId),
      });
    }
    stopWatchingRuns ??= companyPageStore.subscribe(settleManualRuns);
    settleManualRuns();
    return started;
  },
};
